import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import type {Reservation, Reservee} from '@wix/ambassador-table-reservations-v1-reservation/types'
import type {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import type {CreateCartResponse} from '@wix/ambassador-ecom-v2-cart/types'

import {reservationsService} from '../../../services/reservationsService'
import {RequestStatus} from '../../../utils/wrapRequest'
import {goToReservationConfirmation, navigateToCheckoutPage} from '../../../utils/navigation'
import {getLogger} from '../../../utils/getLogger'
import {cartService} from '../../../services/cartService'
import {isCartPaymentRequired} from '../../../utils/ecom'
import {trackTableReservedEvent} from '../../../utils/trackEvent'
import {EXPERIMENTS, TransactionNames} from '../../../utils/constants'
import {isCaptchaCancelledError} from '../../../utils/captchaRetry'

import {finishCartFlow} from './finishCartFlow'
import {isReservationSuccessful, getRefererFromUrlParams} from './utils'

// error codes for which we have separate modal titles
export enum ReserveReservationApplicationErrorCode {
  HELD_EXPIRED = 'HELD_EXPIRED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export const reserveReservation = (flowAPI: ControllerFlowAPI) => {
  return async (
    reservation: Reservation,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
    shouldRequestSkipCheckout: boolean,
    isCancellationPolicyActive: boolean,
  ) => {
    const reserveReservationTransaction = flowAPI.panoramaClient.transaction(
      TransactionNames.reserveReservation,
    )

    reserveReservationTransaction.start({
      params: {
        reservation,
        reservee,
        reservationLocation,
      },
    })

    const setProps = flowAPI.controllerConfig.setProps
    const {getResolvedError} = flowAPI.errorHandler
    const logger = getLogger(flowAPI.bi!)

    try {
      setProps({submitReservationStatus: RequestStatus.LOADING})
      const finishedReservation = await reservationsService.reserveReservation(
        flowAPI,
        reservation.id ?? '',
        reservee,
        reservation.revision ?? '',
      )

      // this is the only place where can get just created reservation
      if (finishedReservation?.id) {
        const referer = getRefererFromUrlParams(flowAPI)

        logger.reservationCreated({
          isPreview: false,
          reservation: finishedReservation!,
          reservee,
          reservationLocation,
          isCardTokenization: false,
          referer,
        })

        await handleCartFlow(
          flowAPI,
          finishedReservation,
          reservee,
          reservationLocation,
          shouldRequestSkipCheckout,
          isCancellationPolicyActive,
        )
      } else {
        throw new Error('No reservation')
      }
    } catch (err: any) {
      // User closed captcha dialog
      if (isCaptchaCancelledError(err)) {
        setProps({submitReservationStatus: RequestStatus.DEFAULT})

        return
      }

      flowAPI.panoramaClient.errorMonitor().reportError(err)

      const resolvedError = getResolvedError(err)

      setProps({
        submitReservationStatus: RequestStatus.FAILED,
        apiRequestErrorDetails: resolvedError,
      })
    }

    reserveReservationTransaction.finish()
  }
}

const handleCartFlow = async (
  flowAPI: ControllerFlowAPI,
  finishedReservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
  shouldRequestSkipCheckout: boolean,
  isCancellationPolicyActive: boolean,
) => {
  const {getResolvedError} = flowAPI.errorHandler
  const createCartTransaction = flowAPI.panoramaClient.transaction(TransactionNames.createCart)

  let cartResponse: CreateCartResponse

  const createCartParams = {
    reservationId: finishedReservation.id!,
    reservee,
    locationId: reservationLocation.location?.id ?? undefined,
  }

  createCartTransaction.start({params: createCartParams})

  try {
    cartResponse = await cartService.createCart(flowAPI, createCartParams)
  } catch (error) {
    flowAPI.panoramaClient.errorMonitor().reportError(error as Error)
    getResolvedError(error)
    if (isReservationSuccessful(finishedReservation)) {
      console.error('Cart is undefined')

      await goToReservationConfirmation({
        flowAPI,
        reservationId: finishedReservation.id!,
      })

      return
    } else {
      throw error
    }
  }

  createCartTransaction.finish({cart: cartResponse})

  if (!cartResponse?.cart) {
    throw new Error('Cart is undefined')
  }

  const placeFreeOrderInOoi = flowAPI.experiments.enabled(
    EXPERIMENTS.tableReservationsSkipCheckoutReplacement,
  )

  if (placeFreeOrderInOoi) {
    await finishCartFlow(
      flowAPI,
      cartResponse.cart.id!,
      finishedReservation,
      isCancellationPolicyActive,
    )

    return
  }

  await navigateToCheckoutPage(flowAPI, cartResponse.cart!.id!, finishedReservation.id!, {
    skipCheckout: shouldRequestSkipCheckout && !isCartPaymentRequired(cartResponse),
  })
}
